import { defineStore } from "pinia";
import { ref, computed } from "vue";
import axios from "@/plugins/axios";
import { showMessage } from "@/utils/notifications";

export const useInvestorStore = defineStore("investor", () => {
  // State
  const investorList = ref([]);
  const loadingInvestors = ref(false);

  // Getters
  const getInvestorList = computed(() => investorList.value);
  const getLoadingInvestors = computed(() => loadingInvestors.value);

  // Actions
  const setInvestorList = (data) => {
    investorList.value = data;
  };

  const addInvestor = (data) => {
    for (var i = 0; i < investorList.value.length; i++) {
      if (investorList.value[i].entity_id == data.entity_id) {
        investorList.value[i] = data;
        return;
      }
    }
    investorList.value.push(data);
  };

  const deleteInvestor = (payload) => {
    let index = investorList.value.findIndex((e) => e.entity_id == payload.entity_id);
    if (index > -1) {
      investorList.value.splice(index, 1);
    }
  };

  // API Actions
  const getInvestors = async () => {
    try {
      const response = await axios.get(`/investor`);
      const resData = response.data;

      if (!resData.success) {
        showMessage({ ...resData, messageType: "error", messageTitle: "Error" });
        return resData;
      }

      setInvestorList(resData.investors);
      return resData;
    } catch (error) {
      console.error("Error fetching investors:", error);
      return { success: false, message: "Failed to fetch investors" };
    }
  };

  const getInvestorByEntityID = async (entity_id) => {
    try {
      const response = await axios.get(`/investor/${entity_id}`);
      const resData = response.data;

      if (!resData.success) {
        showMessage({ ...resData, messageType: "error", messageTitle: "Error" });
        return null;
      } else {
        return resData.investor;
      }
    } catch (error) {
      console.error("Error fetching investor by entity ID:", error);
      return null;
    }
  };

  const addInvestorAction = async (payload) => {
    try {
      const { data } = await axios.post(`/investor`, payload);

      if (!data.success) {
        showMessage({ ...data, messageType: "error", messageTitle: "Error" });
        return data;
      }

      showMessage({ ...data, messageType: "success", messageTitle: "Success" });
      addInvestor(data.investor);
      return true;
    } catch (error) {
      console.error("Error adding investor:", error);
      return { success: false, message: "Failed to add investor" };
    }
  };

  const updateInvestorAddress = async (payload) => {
    try {
      const { data } = await axios.post(`/investor/address`, payload);

      if (!data.success) {
        showMessage({ ...data, messageType: "error", messageTitle: "Error" });
        return data;
      }

      showMessage({ ...data, messageType: "success", messageTitle: "Success" });
      addInvestor(data.investor);
      return true;
    } catch (error) {
      console.error("Error updating investor address:", error);
      return { success: false, message: "Failed to update investor address" };
    }
  };

  const deleteInvestorAction = async (payload) => {
    try {
      const { data } = await axios.delete(`/investor`, {
        data: { entity_id: payload.entity_id },
      });

      if (!data.success) {
        showMessage({ ...data, messageType: "error", messageTitle: "Error" });
        return data;
      }

      showMessage({ ...data, messageType: "success", messageTitle: "Success" });
      deleteInvestor(payload);
      return true;
    } catch (error) {
      console.error("Error deleting investor:", error);
      return { success: false, message: "Failed to delete investor" };
    }
  };

  const getInvestorData = async (payload) => {
    try {
      const response = await axios.get(`/investor/${payload.investor_id}`);
      const resData = response.data;

      if (!resData.success) {
        showMessage({ ...resData, messageType: "error", messageTitle: "Error" });
        return null;
      } else {
        return resData.investor;
      }
    } catch (error) {
      console.error("Error fetching investor data:", error);
      return null;
    }
  };

  return {
    // State
    investorList,
    loadingInvestors,

    // Getters
    getInvestorList,
    getLoadingInvestors,

    // Actions
    setInvestorList,
    addInvestor,
    deleteInvestor,

    // API Actions
    getInvestors,
    getInvestorByEntityID,
    addInvestorAction,
    updateInvestorAddress,
    deleteInvestorAction,
    getInvestorData,
  };
});
